import { cn } from "@/lib/cn";
import { CurrencyMark } from "@/components/ui/CurrencyMark";
import { useCurrencyPresentation } from "@/components/currency/CurrencyPresentationProvider";
import { type FormatSparkOptions } from "@/lib/money";
import { formatCurrency, formatCurrencyText } from "@/currency";
import { campaignCurrency, type CurrencyCode } from "@/lib/currency";

// CurrencyAmount / CurrencyBadge — currency-aware money rendering for the
// operator console, consuming the SM-443 presentation contract.
//
// Two things changed here, and they are the same change viewed twice:
//
//  1. GOLD no longer short-circuits. This component used to hand `code ===
//     "GOLD"` straight to <SparkAmount>, which drew a hardcoded ⚡ from a
//     hardcoded divisor. That made GOLD — the currency every existing wallet
//     was backfilled to (SM-412) — the one currency an operator could not
//     re-skin, however much artwork they uploaded on /currencies.
//
//  2. Presentation comes from the PROVIDER, not from a frozen table. The
//     previous version read CURRENCY_SEED for colour and symbol even on the
//     RED/GREEN path, so operator configuration was ignored there too.
//
// Every currency now renders the same way: resolved presentation in, mark +
// digits out, and the currency's NAME in the accessible label so identity
// never depends on colour alone.

export interface CurrencyAmountProps extends FormatSparkOptions {
  /** Currency code; absent = legacy GOLD (G3 presentation boundary). */
  currency: string | undefined | null;
  value: string | number | undefined | null;
  className?: string;
  /** Colour intent. "auto" turns positive green / negative red (P&L). */
  tone?: "default" | "muted" | "success" | "danger" | "warning" | "primary" | "auto";
  /** Mark edge length in px (defaults to ~0.92em). */
  iconSize?: number;
  /** Hide the mark (e.g. a column already headed with the currency). */
  hideIcon?: boolean;
  iconClassName?: string;
}

const TONE: Record<NonNullable<CurrencyAmountProps["tone"]>, string> = {
  default: "",
  muted: "text-muted",
  success: "text-success",
  danger: "text-danger",
  warning: "text-warning",
  primary: "text-neon",
  auto: "",
};

export function CurrencyAmount({
  currency,
  value,
  className,
  tone = "default",
  iconSize,
  hideIcon = false,
  iconClassName,
  ...fmt
}: CurrencyAmountProps) {
  const code = campaignCurrency(currency);
  const presentation = useCurrencyPresentation(code);

  // The ORIGINAL wire integer goes through — no pre-division. Rescaling before
  // the formatter (`minor / (unitMinor / 100)`) hands it a float, and its
  // integer path starts with String(Math.trunc(...)), so every sub-cent digit
  // would vanish at dp > 2 (SM-484).
  const text = formatCurrency(presentation, value ?? null, fmt);
  const label = formatCurrencyText(presentation, value ?? null, fmt);
  const placeholder = fmt.placeholder ?? "—";
  const isPlaceholder = text === placeholder;

  let toneClass = TONE[tone];
  if (tone === "auto" && !isPlaceholder) {
    const n = typeof value === "number" ? value : Number(value);
    toneClass = n > 0 ? "text-success" : n < 0 ? "text-danger" : "text-muted";
  }

  if (isPlaceholder || hideIcon) {
    return (
      <span className={cn("tabular-nums", toneClass, className)} aria-label={isPlaceholder ? undefined : label}>
        {text}
      </span>
    );
  }

  // Split a leading sign so the mark hugs the magnitude: −⚡12.80, not ⚡−12.80.
  let sign = "";
  let body = text;
  if (text[0] === "−" || text[0] === "+") {
    sign = text[0];
    body = text.slice(1);
  }

  return (
    <span
      aria-label={label}
      className={cn("inline-flex items-center gap-[0.12em] tabular-nums", toneClass, className)}
    >
      <span className="inline-flex items-center">
        {sign}
        <CurrencyMark presentation={presentation} size={iconSize} className={iconClassName} />
      </span>
      {body}
    </span>
  );
}

/**
 * Compact currency identity pill for table rows and detail headers.
 *
 * Carries the MARK as well as the symbol (SM-491). This pill is the currency
 * column of every financial table, and it was the one identity surface in the
 * console that stayed text-only however much artwork an operator uploaded —
 * so a row said "GOLD" while the balance beside it wore the configured coin.
 * The symbol stays: it is what keeps the pill readable in monochrome, and it
 * is the same configured symbol the mark degrades to.
 */
export function CurrencyBadge({
  currency,
  className,
}: {
  currency: string | undefined | null;
  className?: string;
}) {
  const code = campaignCurrency(currency);
  const presentation = useCurrencyPresentation(code);
  const { color, symbol, displayName } = presentation;
  return (
    <span
      title={displayName}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-2xs font-semibold",
        className,
      )}
      style={{ color, borderColor: `${color}55`, backgroundColor: `${color}14` }}
    >
      <CurrencyMark presentation={presentation} />
      {symbol}
    </span>
  );
}

/** The mark on its own, for legends and pickers. */
export function CurrencyMonogram({
  currency,
  className,
}: {
  currency: CurrencyCode;
  className?: string;
}) {
  const presentation = useCurrencyPresentation(currency);
  return <CurrencyMark presentation={presentation} className={className} />;
}
